import { DeliveryInvoiceDetailType, DeliveryInvoiceSkuDetails, LoadOutDetail, LoadoutDetailsType, LoadOutItem, ReturnDetailsObject, SalesReasonDetails } from "../../types/types";
import { ChannelkartNetworkGet, ChannelkartNetworkPut, ChannelkartNetworkPost } from "../../services/networkService";
import { getDataFromLocalDb, getModel, insertDataIntoLocalDB } from "../../services/dbService";
import { EnvironmentRegistry } from "../../services/registry/environmentRegistry";
import moment from "moment";
import { getMetaData, getMetaDataConfig } from "../../services/uiConfigService";
import { NavigateFunction, Route } from "react-router-dom";
import { getLoadoutList, getSingleLoadout } from "../../services/loadoutService";
import { getOutletDetails } from "../createSecondaryOrder/CreateSecondaryOrderService";

// API call to fetch delivery data
export const fetchDeliverySummary = async (startDate?: string, endDate?: string): Promise<DeliveryInvoiceDetailType[]> => {
    try {
        const response = await ChannelkartNetworkGet(`${EnvironmentRegistry.getloadoutBaseUrl()}/v1/loadoutDetails/settlement?startDate=${startDate} 00:00:00&endDate=${endDate} 23:59:59`);
        const invoices = response.data.map((x: any) => x.invoiceNumber);
        const invoiceDetails = invoices.length ? (await ChannelkartNetworkGet(`/v3/sales?includeSalesDetails=true&filter=invoiceNumber[in]:[${invoices}]`)).data.features : [];
        const statusMap: any = {};
        invoiceDetails.forEach((invoice: any) => { statusMap[invoice.invoiceNumber] = invoice.status });
        const outletDetails = await getOutletDetailsMap();
        return response.data.map((load: any) => {
            const outletName = outletDetails[load.outletCode]?.outletName ?? "";
            const gstApplicable = outletDetails[load.outletCode]?.extendedAttributes?.gstApplicable;
            const invoice: DeliveryInvoiceDetailType = {
                isEInvoiced: true,
                loginId:"",
                source:"",
                invoiceNumber: load.invoiceNumber,
                status: statusMap[load.invoiceNumber],
                loadNumber: load.loadNumber,
                deliveryDate: load.deliveryData,
                modeOfDelivery: load.modeOfDelivery,
                outletName: outletName,
                outletCode: load.outletCode,
                skuCount: load.skus,
                amount: load.amount,
                taxAmount: load.salesInfo?.taxAmount ?? 0,
                discountAmount: load.salesInfo?.discountAmount ?? 0,
                finalAmount: load.salesInfo?.finalAmount ?? 0,
                gstApplicable: gstApplicable,
                hasGstin: gstApplicable,
                outletNameCode: `${outletName} (${load.outletCode})${gstApplicable ? " (GST)" : ""}`,
                extendedAttributes:{},
                salesInfo:{}
            };
            return invoice;
        })
    } catch (error) {
        console.error("Error fetching delivery summary:", error);
        throw error;
    }
};

const calculateTaxValue = (invoice: any) => {
    const tax = invoice.salesDetails?.map((sale: any) =>
        sale.extendedAttributes?.taxInfo ?? []
    ).flat().reduce((sum: number, tax: any) =>
        sum + (tax?.taxAmount ?? 0)
        , 0).toFixed(2);
    return tax;
}

export const fetchDeliverySummaryWithInvoice = async (startDate?: string, endDate?: string): Promise<DeliveryInvoiceDetailType[]> => {
    try {
        const invoiceDetails = (await ChannelkartNetworkGet(`/delivery/sales?size=1000&includeSalesDetails=true&filter=deliveryDate[gte]:${moment(startDate + " 00:00:00").utc().format("YYYY-MM-DD HH:mm:ss")} and deliveryDate[lte]:${moment(endDate + " 23:59:59").utc().format("YYYY-MM-DD HH:mm:ss")}&transformerOut=DeliveryInfoAddAdditionalData`)).data;
        const outletDetails = await getOutletDetailsMap();
        return invoiceDetails.features.map((invoice: any) => {
            const outletName = outletDetails[invoice.outletCode]?.outletName ?? "";
            const gstApplicable = outletDetails[invoice.outletCode]?.extendedAttributes?.gstApplicable == "true";
            const taxAmount = calculateTaxValue(invoice)
            const deliveryDate = invoice.deliveryDate ? moment(new Date(invoice.deliveryDate)).format("DD-MM-YYYY") : "";
            return {
                invoiceNumber: invoice.invoiceNumber,
                status: invoice.status,
                loadNumber: "",
                deliveryDate: deliveryDate,
                modeOfDelivery: "",
                outletName: outletName,
                outletCode: invoice.outletCode,
                skuCount: invoice.salesDetails?.length ?? 0,
                amount: invoice.billAmount,
                taxAmount: taxAmount,
                discountAmount: 0,
                finalAmount: invoice.netAmount,
                gstApplicable: gstApplicable ? true : false,
                outletNameCode: `${outletName} (${invoice.outletCode})${gstApplicable ? " (GST)" : ""}`
            } as DeliveryInvoiceDetailType;
        })
    } catch (error) {
        console.error("Error fetching delivery summary:", error);
        throw error;
    }
};

export const getOutletDetailsMap = async (outletCodeList?: string[], salesrep?:string) => {
  try {
  let allData: any[] = [];
  if(salesrep){
      let page = 0;
      const size=1000;
    
      while (true) {
        const response = await ChannelkartNetworkGet(`/outletDetails?minimalResponse=true${salesrep ? '&loginId='+salesrep : ""}&filter=activeStatus:active${outletCodeList ? ` and outletCode[in]:[${outletCodeList.join(",")}]` : ""}&size=${size}&page=${page}&source=portal`);
        const data = response.data;
    
        allData = allData.concat(data.features);
        page++;
        if (data.features.length < size) {
          break
        } 
      }
  }else{
    allData = await getOutletDetails();
  }

  const outletDetails: any = {};
    allData.forEach((feature: any) => {
    //   const immediateParent = feature['parent-0-0-id'];
      outletDetails[feature.outletCode] = {
        outletName: feature.outletName,
        outletCode: feature.outletCode,
        // salesRepId: immediateParent,
        gstNo: feature?.extendedAttributes?.gstin,
        creationTime: feature.creationTime,
        keyAccount: feature?.keyAccount,
        routeId: feature?.beat,
        routeName: feature?.beatName,
        activeStatus: feature?.activeStatus, // Preserve activeStatus field
      }
    });
    return outletDetails;
  } catch (error) {
    console.error("Error fetching external API data:", error);
    return {};
  }
}


export const enrichUpdatedReturnData = async (returnDetailsArray: DeliveryInvoiceSkuDetails[], batchDetails: { [key: string]: { [key: string]: SalesReasonDetails[] } }) => {
    let productData: any = {};
    try {
        productData = (await getDataFromLocalDb("Product")).data[0].data;
    } catch (e) { }
    returnDetailsArray.forEach(invoiceSkuObject => {
        if (!(batchDetails && batchDetails[invoiceSkuObject.batchCode])) {
            return;
        }
        let cq = 0;
        let pq = 0;
        let oq = 0;
        invoiceSkuObject.batchIds.forEach(batchObject => {
            if (batchDetails[invoiceSkuObject.batchCode][batchObject.batchId]) {
                batchObject.reasonDetails = batchDetails[invoiceSkuObject.batchCode][batchObject.batchId]
                cq += batchObject.reasonDetails.reduce((sum, reasonObject) => sum + reasonObject.caseQty, 0);
                pq += batchObject.reasonDetails.reduce((sum, reasonObject) => sum + reasonObject.pieceQty, 0);
                oq += batchObject.reasonDetails.reduce((sum, reasonObject) => sum + reasonObject.otherQty, 0);
            }
        })
        const convertedQtyData = getConvertedQty({
            caseQty: invoiceSkuObject.totalCaseQty,
            pieceQty: invoiceSkuObject.totalPieceQty,
            otherQty: invoiceSkuObject.totalOtherQty
        }, {
            caseQty: cq,
            pieceQty: pq,
            otherQty: oq
        }, productData[invoiceSkuObject.batchCode]
        );
        invoiceSkuObject.returnCaseQty = cq;
        invoiceSkuObject.returnOtherQty = oq;
        invoiceSkuObject.returnPieceQty = pq;
        invoiceSkuObject.soldCaseQty = convertedQtyData.caseQty;
        invoiceSkuObject.soldOtherQty = convertedQtyData.otherQty;
        invoiceSkuObject.soldPieceQty = convertedQtyData.pieceQty;
    })
}

export const getSalesReturnDetails = async (invoiceNumber: string, loadoutItems?:{[key:string]:LoadOutItem}) => {

    let productData: any = {};
    try {
        productData = (await getDataFromLocalDb("Product")).data[0].data;
    } catch (e) { }
    const invoiceDetails = (await ChannelkartNetworkGet(`/v3/sales?includeSalesDetails=true&filter=invoiceNumber[in]:[${invoiceNumber}]`)).data.features
    const returnDetails:any[] = [];
    // const returnDetails = invoiceDetails[0].status.toLowerCase() == "out for delivery" ? [] : (await ChannelkartNetworkGet(`/v3/sales?includeSalesDetails=true&filter=referenceNumber[in]:[${invoiceNumber}]`)).data.features
    // const returnDetails = (await ChannelkartNetworkGet(`/v3/sales?includeSalesDetails=true&filter=referenceNumber[in]:[${invoiceNumber}]`)).data.features
    const salesDetails = invoiceDetails[0].salesDetails;
    const isCancelled = invoiceDetails[0].status === "CANCELLED";
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
    const batchIdConfiguration = ConfigDictionary.batchIdConfiguration;
    const returnDetailsArray: DeliveryInvoiceSkuDetails[] = salesDetails.map((details: any) => {
        if(loadoutItems){
            const currentLoadoutItem = loadoutItems[batchIdConfiguration ? details.batchCode+"_"+details.batchId : details.batchCode];
            if(!currentLoadoutItem){
                return null;
            }
            const convertedQtyData = getConvertedQty({
                caseQty: currentLoadoutItem.caseQty,
                pieceQty: currentLoadoutItem.pieceQty,
                otherQty: currentLoadoutItem.otherQty
            }, {
                caseQty: currentLoadoutItem.caseQtyLeft,
                pieceQty: currentLoadoutItem.pieceQtyLeft,
                otherQty: currentLoadoutItem.otherQtyLeft
            },productData[details.batchCode]);
            return {
                id: details.id,
                skuCode: details.skuCode,
                batchCode: details.batchCode,
                skuName: productData[details.batchCode].skuDescription,
                totalPieceQty: currentLoadoutItem.pieceQty,
                totalCaseQty: currentLoadoutItem.caseQty,
                totalOtherQty: currentLoadoutItem.otherQty,
                soldPieceQty: convertedQtyData.pieceQty,
                soldCaseQty: convertedQtyData.caseQty,
                soldOtherQty: convertedQtyData.otherQty,
                returnPieceQty: currentLoadoutItem.pieceQtyLeft,
                returnCaseQty: currentLoadoutItem.caseQtyLeft,
                returnOtherQty: currentLoadoutItem.otherQtyLeft,
                ptr: details.price,
                returnAmount: 0,
                batchIds: details.batchIds,
                batchId: details.batchId,
                extendedAttributes: details.extendedAttributes
            }
        }
        return {
            id: details.id,
            skuCode: details.skuCode,
            batchCode: details.batchCode,
            skuName: productData[details.batchCode].skuDescription,
            totalPieceQty: details.initialPieceQuantity,
            totalCaseQty: details.caseQuantity,
            totalOtherQty: details.otherUnitQuantity,
            soldPieceQty: isCancelled ? 0: details.initialPieceQuantity,
            soldCaseQty: isCancelled ? 0 : details.caseQuantity,
            soldOtherQty: isCancelled ? 0 : details.otherUnitQuantity,
            returnPieceQty: isCancelled ? details.initialPieceQuantity : 0,
            returnCaseQty: isCancelled ? details.caseQuantity : 0,
            returnOtherQty: isCancelled ? details.otherUnitQuantity : 0,
            ptr: details.price,
            returnAmount: 0,
            batchIds: details.batchIds,
            batchId: details.batchId,
            extendedAttributes: details.extendedAttributes
        }
    }).filter(Boolean);
    let returnDetailsObject: ReturnDetailsObject | null = null;
    if (returnDetails.length) {
        const returnData: any = {};
        returnDetails[0].salesDetails.forEach((sales: any) => { returnData[sales.batchCode] = sales; });
        returnDetailsArray.forEach((returnObject: any) => {
            const returnSales = returnData[returnObject.batchCode];
            if (!returnSales) {
                return;
            }
            const netAmount = Math.abs(returnSales.netAmount);
            const initialPieceQuantity = Math.abs(returnSales.initialPieceQuantity);
            const caseQuantity = Math.abs(returnSales.caseQuantity);
            const otherUnitQuantity = Math.abs(returnSales.otherUnitQuantity);
            const convertedQtyData = getConvertedQty({
                caseQty: returnObject.soldCaseQty,
                pieceQty: returnObject.soldPieceQty,
                otherQty: returnObject.soldOtherQty
            }, {
                caseQty: caseQuantity,
                pieceQty: initialPieceQuantity,
                otherQty: otherUnitQuantity
            }, productData[returnObject.batchCode]
            )

            returnObject.soldPieceQty = convertedQtyData.pieceQty;
            returnObject.soldCaseQty = convertedQtyData.caseQty;
            returnObject.soldOtherQty = convertedQtyData.otherQty;
            returnObject.returnPieceQty = initialPieceQuantity;
            returnObject.returnCaseQty = caseQuantity;
            returnObject.returnOtherQty = otherUnitQuantity;
            returnObject.returnAmount = netAmount;
        })
        returnDetailsObject = {
            returnAmount: Math.abs(returnDetails[0].netAmount),
            returnId: returnDetails[0].invoiceNumber,
            outletCode: returnDetails[0].outletCode
        };
    }
    // setAllReturnedCombinedData(returnDetailsArray);
    // if(returnDetailsObject){
    //   setReturnDetails(returnDetailsObject)
    // }
    return { returnDetailsArray, returnDetailsObject, returnSalesDetails: returnDetails[0]?.salesDetails };
}

interface QuantityObject {
    caseQty: number
    pieceQty: number
    otherQty: number
}
type QtyType = 'caseQty' | 'pieceQty' | 'otherQty';
type ConversionQtyMapType = { [key in QtyType]?: { [key in QtyType]?: number } }

const createConversionQtyMap = (productDetails: any) => {
    const conversionMap: ConversionQtyMapType = {
        'caseQty': {
            'pieceQty': productDetails && productDetails.caseToPieceQuantity ? productDetails.caseToPieceQuantity : 0,
            'otherQty': productDetails && productDetails.caseToOtherUnitQuantity ? productDetails.caseToOtherUnitQuantity : 0
        },
        'otherQty': {
            'pieceQty': productDetails && productDetails.otherUnitToPieceQuantity ? productDetails.otherUnitToPieceQuantity : 0,
        },
    }
    return conversionMap;
}

const convertAndTransferHigherQty = (totalQtyData: QuantityObject, updatedQtyData: QuantityObject, fromQtyType: QtyType, toQtyType: QtyType, conversionMap: any) => {
    if (!conversionMap || !conversionMap[fromQtyType] || !conversionMap[fromQtyType][toQtyType]) {
        return;
    }
    const qtyRequired = Math.ceil(updatedQtyData[toQtyType] / conversionMap[fromQtyType][toQtyType]);
    const qtyDeducted = qtyRequired > totalQtyData[fromQtyType] ? totalQtyData[fromQtyType] : qtyRequired;
    totalQtyData[fromQtyType] -= qtyDeducted;
    totalQtyData[toQtyType] += qtyDeducted * conversionMap[fromQtyType][toQtyType];
}

const convertAndTransferLowerQty = (totalQtyData: QuantityObject, updatedQtyData: QuantityObject, fromQtyType: QtyType, toQtyType: QtyType, conversionMap: any) => {
    if (!conversionMap || !conversionMap[toQtyType] || !conversionMap[toQtyType][fromQtyType]) {
        return;
    }
    const qtyRequired = updatedQtyData[toQtyType] * conversionMap[toQtyType][fromQtyType];
    const qtyDeducted = qtyRequired > totalQtyData[fromQtyType] ? totalQtyData[fromQtyType] : qtyRequired;
    totalQtyData[fromQtyType] -= qtyDeducted;
    totalQtyData[toQtyType] += qtyDeducted / conversionMap[toQtyType][fromQtyType];
}

export const getConvertedQty = (totalQtyData: QuantityObject, updatedQtyData: QuantityObject, productDetails: any) => {
    const convQtyMap =  createConversionQtyMap(productDetails);
    const qtyHeirarchy: QtyType[] = ['caseQty', 'otherQty', 'pieceQty'];
    qtyHeirarchy.forEach((qtyType, index) => {
        if (updatedQtyData[qtyType] == 0) {
            return;
        }
        deductQty(totalQtyData, updatedQtyData, qtyType)
        for (let i = index - 1; i >= 0; i--) {
            convertAndTransferHigherQty(totalQtyData, updatedQtyData, qtyHeirarchy[i], qtyType, convQtyMap);
            deductQty(totalQtyData, updatedQtyData, qtyType);
            if (updatedQtyData[qtyType] == 0) {
                return;
            }
        }
        for (let i = index + 1; i < qtyHeirarchy.length; i++) {
            convertAndTransferLowerQty(totalQtyData, updatedQtyData, qtyHeirarchy[i], qtyType, convQtyMap);
            deductQty(totalQtyData, updatedQtyData, qtyType);
            if (updatedQtyData[qtyType] == 0) {
                return;
            }
        }
    })
    return totalQtyData;
}

const deductQty = (totalQtyData: QuantityObject, updatedQtyData: QuantityObject, qtyType: QtyType) => {
    const qtyDeducted = totalQtyData[qtyType] > updatedQtyData[qtyType] ? updatedQtyData[qtyType] : totalQtyData[qtyType];
    totalQtyData[qtyType] -= qtyDeducted;
    updatedQtyData[qtyType] -= qtyDeducted;
}

export const getCreditAmount = async (returnId: string) => {
    try {
        const creditAmountResponse = (await ChannelkartNetworkGet(`/creditNote/getAvailableCredit?returnId=${returnId}`)).data;
        return (creditAmountResponse.amount ?? 0) as number;
    } catch (e) {
        return 0;
    }
}

export const submitCreditNote = async (reqBody: any) => {
    const creditAmountResponse = (await ChannelkartNetworkPost(`/creditNote`, reqBody));
    return creditAmountResponse;
}

export const handlePartialDelivery = (data: DeliveryInvoiceSkuDetails[], invoiceSummary: DeliveryInvoiceDetailType) => {
    const user = JSON.parse(localStorage.authContext).user;
    const reqBody = {
        "referenceNumber": invoiceSummary.invoiceNumber,
        "supplier": user.loginId,
        "loginId": user.loginId,
        "outletCode": invoiceSummary.outletCode,
        "status": "PARTIAL DELIVERED",
        "salesDetails": data.map((salesDetails) => {
            if (salesDetails.returnPieceQty == 0 && salesDetails.returnCaseQty == 0 && salesDetails.returnOtherQty == 0) {
                return null
            }
            return {
                "skuCode": salesDetails.skuCode,
                "batchCode": salesDetails.batchCode,
                "pieceQuantity": salesDetails.returnPieceQty,
                "caseQuantity": salesDetails.returnCaseQty,
                "otherUnitQuantity": salesDetails.returnOtherQty,
                "batchIds": salesDetails.batchIds.map((batchData) => {
                    const caseQty = batchData.reasonDetails ? batchData.reasonDetails.reduce((sum, y) => sum + y.caseQty, 0) : 0;
                    const pieceQty = batchData.reasonDetails ? batchData.reasonDetails.reduce((sum, y) => sum + y.pieceQty, 0) : 0;
                    const otherQty = batchData.reasonDetails ? batchData.reasonDetails.reduce((sum, y) => sum + y.otherQty, 0) : 0;
                    return {
                        "batchId": batchData.batchId,
                        "caseQty": caseQty,
                        "otherQty": otherQty,
                        "pieceQty": pieceQty,
                        "reasonDetails": batchData.reasonDetails
                    }
                })
            };
        }).filter(Boolean)
    }
    return ChannelkartNetworkPut("/v1/sales/partialDelivered", reqBody);

}

export const fetchLoadoutDetails = async (startDate: string, endDate: string) => {
    startDate = moment(startDate).utc().format("YYYY-MM-DD HH:mm:ss");
    endDate = moment(endDate + " 23:59:59").utc().format("YYYY-MM-DD HH:mm:ss");
    
    // Initialize variables for pagination
    let allLoadouts: any[] = [];
    let page = 0;
    const size=1000;
    let hasMoreData = true;
    
    // Fetch data with pagination using getLoadoutList function
    while (hasMoreData) {
        const response = await getLoadoutList(startDate, endDate, true, page,size);
        const content = response?.data?.content || [];
        
        if (content.length != size) {
            hasMoreData = false; 
        }
        
        allLoadouts = [...allLoadouts, ...content];
        page++; 
    }
    allLoadouts.sort((a: any, b: any) => {
        return moment(b.loadOutDate).unix() - moment(a.loadOutDate).unix();
    })
    let productData: any = {};
    let salesmanData: any = {};
    try {
        productData = (await getDataFromLocalDb("Product")).data[0].data;
    } catch (e) { }
    try{
        const loginId = JSON.parse(localStorage.authContext).user.loginId;  
        const salesmanResponse = await ChannelkartNetworkGet(`/users/${loginId}/salesman`);
        salesmanResponse.data.features.forEach((salesman:any)=>{
            salesmanData[salesman.loginId] = salesman.name;
        })
    }catch(e){}
    
    const loadoutData = allLoadouts.map((loadout: LoadoutDetailsType) => {
        const soldQtyData = calculateSoldLoadoutQty(loadout, productData);
        const isActualQtyEditable = getIsActualQtyEditable(loadout);
        const isActualQtyDisabled = loadout.status.toLowerCase() != "delivered";
        const actualQtyData = calculateActualQty(loadout);
        return {
            ...loadout,
            salesmanName:salesmanData[loadout.salesmanId],
            loadOutDate: moment(loadout.loadOutDate).format("DD-MM-YYYY"),
            soldCaseQty: soldQtyData.caseQty,
            soldPieceQty: soldQtyData.pieceQty,
            soldOtherQty: soldQtyData.otherQty,
            actualPieceReturnedQty: actualQtyData.pieceQty,
            actualCaseReturnedQty: actualQtyData.caseQty,
            actualOtherReturnedQty: actualQtyData.otherQty,
            isActualQtyEditable,
            isActualQtyDisabled,
            shortageUpdated: loadout.shortageUpdated
        };
    });

    return loadoutData as LoadoutDetailsType[];
}


const getIsActualQtyEditable=(loadout:LoadoutDetailsType)=>{
    const returnQtySum = loadout.totalCaseLeftQty+loadout.totalPieceLeftQty+loadout.totalOtherLeftQty;
    return returnQtySum > 0 && loadout.settlementDate && moment(loadout.settlementDate).add(24, 'hours').isAfter(moment())? true : false ;
}

export const fetchLoadoutDetailsWithLoadNumber = async (loadoutDate: string, loadNumber: string) => {
    const response = await getSingleLoadout(loadNumber, true);
    response.data.content[0].loadOutDetails.forEach((loadoutDetail:any)=>{
        loadoutDetail.salesInfo.invoiceStatus = ["out for delivery", "partially invoiced"].includes(loadoutDetail.salesInfo.invoiceStatus.toLowerCase()) ? "INVOICED" : loadoutDetail.salesInfo.invoiceStatus;
    });
    const outletDetails = await getOutletDetailsMap(response.data.content[0].loadOutDetails.map((x:any)=>x.outletCode));
    let productData: any = {};
    try {
        productData = (await getDataFromLocalDb("Product")).data[0].data;
    } catch (e) { }
    let loadoutDetailsStatus: any;
    const loadoutData = response.data.content[0]
    if(loadoutData.status.toLowerCase() == "delivered"){
        loadoutDetailsStatus = {};
    }else{
        loadoutDetailsStatus = await getLoadoutDetailsStatusFromDb(loadNumber);
        if(loadoutDetailsStatus.length == 0){
            loadoutDetailsStatus = loadoutData.loadOutDetails.reduce((acc:any, invoiceDetail:LoadOutDetail)=>{
                acc[invoiceDetail.invoiceNumber] = "DELIVERED";
                return acc;
            }, {});
            await setLoadoutDetailsStatusInDb(loadNumber, loadoutDetailsStatus);
        }else{
            loadoutDetailsStatus = loadoutDetailsStatus[0].data;
        }
    }
    loadoutData.loadOutDetails.forEach((invoiceDetail: LoadOutDetail) => {
        const soldQty = calculateSoldLoadoutDetailQty(invoiceDetail,productData);
        invoiceDetail.soldCaseQty = soldQty.caseQty;
        invoiceDetail.soldOtherQty = soldQty.otherQty;
        invoiceDetail.soldPieceQty = soldQty.pieceQty;
        invoiceDetail.outletName = outletDetails[invoiceDetail.outletCode]?.outletName ?? "";
        invoiceDetail.gstApplicable = outletDetails[invoiceDetail.outletCode]?.extendedAttributes?.gstApplicable == "true";
        invoiceDetail.id = invoiceDetail.invoiceNumber;
        invoiceDetail.discountAmount = invoiceDetail.salesInfo.discountAmount ?? 0;
        invoiceDetail.deliveryDate = moment(loadoutData.loadOutDate).format("DD-MM-YYYY");
        invoiceDetail.skuCount = invoiceDetail.loadOutItems.length;
        invoiceDetail.invoiceStatus = invoiceDetail.salesInfo.invoiceStatus;
        invoiceDetail.taxAmount = invoiceDetail.salesInfo.gstAmount;
        invoiceDetail.modeOfDelivery = loadoutData.type;
        invoiceDetail.finalAmount = invoiceDetail.salesInfo.finalAmount ?? 0;
        invoiceDetail.invoiceDraftStatus = invoiceDetail.salesInfo.invoiceStatus == "INVOICED" ? loadoutDetailsStatus[invoiceDetail.invoiceNumber] : invoiceDetail.salesInfo.invoiceStatus;
        invoiceDetail.redirectIcon= invoiceDetail.invoiceStatus == "INVOICED" ? "viewAndEditIcon" : "eyeIcon";
        invoiceDetail.salesmanId= invoiceDetail.presellerId;  //doubt
    });
    
    // Sort loadout details based on priority
    loadoutData.loadOutDetails.sort((a: LoadOutDetail, b: LoadOutDetail) => {
        return getPriority(a) - getPriority(b);
    });
    return loadoutData;
}

const getPriority = (invoiceDetail:LoadOutDetail)=>{
    if(invoiceDetail.invoiceDraftStatus == "CANCELLED" || invoiceDetail.invoiceStatus == "CANCELLED"){
        return 3;
    }
    if(invoiceDetail.invoiceDraftStatus == "DELIVERED" || invoiceDetail.invoiceStatus == "DELIVERED"){
        return 2;
    }
    if(invoiceDetail.invoiceDraftStatus == "PARTIAL DELIVERED" || invoiceDetail.invoiceStatus == "PARTIAL DELIVERED"){
        return 1;
    }
    return 10;
}

const calculateSoldLoadoutDetailQty = (invoiceDetail:LoadOutDetail, productData:any)=>{
    const soldQty = {
        caseQty: 0,
        pieceQty: 0,
        otherQty: 0
    };

    if (invoiceDetail.salesInfo.invoiceStatus.toLowerCase() == "invoiced") {
        return soldQty;
    }

    invoiceDetail.loadOutItems.forEach(skuDetails => {
        const convertedQtyData = getConvertedQty({
            caseQty: skuDetails.caseQty,
            pieceQty: skuDetails.pieceQty,
            otherQty: skuDetails.otherQty
        }, {
            caseQty: skuDetails.caseQtyLeft,
            pieceQty: skuDetails.pieceQtyLeft,
            otherQty: skuDetails.otherQtyLeft
        }, productData[skuDetails.batchCode]
        );
        soldQty.caseQty += convertedQtyData.caseQty;
        soldQty.pieceQty += convertedQtyData.pieceQty;
        soldQty.otherQty += convertedQtyData.otherQty;
    })

    return soldQty;
}

const calculateActualQty = (loadout: LoadoutDetailsType) => {
    if(loadout.status.toLowerCase() == "delivered" && loadout.shortageUpdated){
        return {
            caseQty: (loadout.totalCaseLeftQty??0)-(loadout.caseShortage??0),
            pieceQty: (loadout.totalPieceLeftQty??0)-(loadout.pieceShortage??0),
            otherQty: (loadout.totalOtherLeftQty??0)-(loadout.otherShortage??0)
        }
    }
    return {
        caseQty: 0,
        pieceQty: 0,
        otherQty: 0
    };
}


const calculateSoldLoadoutQty = (loadout: LoadoutDetailsType, productData: any) => {
    const soldQty = {
        caseQty: 0,
        pieceQty: 0,
        otherQty: 0
    }
    loadout.loadOutDetails.forEach(invoiceDetail => {
        invoiceDetail.id = invoiceDetail.invoiceNumber;
        if (invoiceDetail.salesInfo.invoiceStatus.toLowerCase() == "invoiced" || invoiceDetail.salesInfo.invoiceStatus.toLowerCase() == "out for delivery") {
            return;
        }
        invoiceDetail.loadOutItems.forEach(skuDetails => {
            const convertedQtyData = getConvertedQty({
                caseQty: skuDetails.caseQty,
                pieceQty: skuDetails.pieceQty,
                otherQty: skuDetails.otherQty
            }, {
                caseQty: skuDetails.caseQtyLeft,
                pieceQty: skuDetails.pieceQtyLeft,
                otherQty: skuDetails.otherQtyLeft
            }, productData[skuDetails.batchCode]
            );
            soldQty.caseQty += convertedQtyData.caseQty;
            soldQty.pieceQty += convertedQtyData.pieceQty;
            soldQty.otherQty += convertedQtyData.otherQty;
        })
    });
    return soldQty;
}

export const getLoadoutReqBody = async (loadoutDetails: LoadoutDetailsType, invoiceNumbers:string[], status?:string, batchDetails?:{[key:string]:{[key:string]:SalesReasonDetails[]}} | null) => {
    let productData: any = {};
    try {
        productData = (await getDataFromLocalDb("Product")).data[0].data;
    } catch (e) { }
    try {
        const reqBody = {
            "loadNumber": loadoutDetails.loadNumber,
            "deliveryStartDate": loadoutDetails.deliveryStartDate,
            "deliveryEndDate": loadoutDetails.deliveryEndDate,
            "invoiceCreationStartDate": loadoutDetails.invoiceCreationStartDate,
            "invoiceCreationEndDate": loadoutDetails.invoiceCreationEndDate,
            "vehicleId": loadoutDetails.vehicleId,
            "supplier": loadoutDetails.supplier,
            "loadoutSource": loadoutDetails.loadoutSource,
            "status": loadoutDetails.status,
            "loadOutDetails": loadoutDetails.loadOutDetails.filter(loadout=>invoiceNumbers.includes(loadout.id)).map(loadout=>{
                return {
                    "loadNumber": loadout.loadNumber,
                    "invoiceNumber": loadout.invoiceNumber,
                    "outletCode": loadout.outletCode,
                    "presellerId": loadout.presellerId,
                    "salesInfo": {...loadout.salesInfo, "invoiceStatus": status ?? loadout.invoiceDraftStatus},
                    "loadOutItems": loadout.loadOutItems.map(loadoutItem=>{
                        return getLoadoutItem(loadoutItem, status ?? loadout.invoiceDraftStatus ?? "DELIVERED",productData, batchDetails);
                    })
                };
            })
        }
        return reqBody;
    } catch (e) {

    }
    return;
}

const getLoadoutItem = (loadoutItem:LoadOutItem, status:string, productData:any, batchDetails?:{[key:string]:{[key:string]:SalesReasonDetails[]}} | null)=>{
    
    const updatedItem:any = {
        "loadOutDetailsId": loadoutItem.loadOutDetailsId,
        "skuCode": loadoutItem.skuCode,
        "batchCode": loadoutItem.batchCode,
        "batchId": loadoutItem.batchId,
        "mrp": loadoutItem.mrp,
        "caseQty":loadoutItem.caseQty,
        "pieceQty":loadoutItem.pieceQty,
        "otherQty":loadoutItem.otherQty,
        "soldCaseQty":loadoutItem.caseQty,
        "soldPieceQty":loadoutItem.pieceQty,
        "soldOtherQty":loadoutItem.otherQty,
        "caseQtyLeft":0,
        "pieceQtyLeft":0,
        "otherQtyLeft":0,
        "itemType": loadoutItem.itemType
    };
    switch (status.toLowerCase()){ 
        case "cancelled":
            updatedItem.caseQtyLeft=loadoutItem.caseQty;
            updatedItem.pieceQtyLeft=loadoutItem.pieceQty;
            updatedItem.otherQtyLeft=loadoutItem.otherQty;
            updatedItem.soldCaseQty=0;
            updatedItem.soldPieceQty=0;
            updatedItem.soldOtherQty=0;
            break;
        case "partial delivered":
            const updatedQty = getUpdatedLoadoutItemQty(loadoutItem,batchDetails);
            updatedItem.caseQtyLeft= updatedQty.caseQty;
            updatedItem.pieceQtyLeft=updatedQty.pieceQty;
            updatedItem.otherQtyLeft=updatedQty.otherQty;
            const soldQty =  getConvertedQty({
                caseQty: loadoutItem.caseQty,
                pieceQty: loadoutItem.pieceQty,
                otherQty: loadoutItem.otherQty
            }, {
                caseQty: updatedQty.caseQty,
                pieceQty: updatedQty.pieceQty,
                otherQty: updatedQty.otherQty
            }, productData[loadoutItem.batchCode]
            );
            updatedItem.soldCaseQty=soldQty.caseQty;
            updatedItem.soldPieceQty=soldQty.pieceQty;
            updatedItem.soldOtherQty=soldQty.otherQty;
            updatedItem.reasonDetails=updatedQty.reasonDetails;
            break;
    }
    return updatedItem;
}

const getUpdatedLoadoutItemQty=(loadoutItem:LoadOutItem, batchDetails?:{[key:string]:{[key:string]:SalesReasonDetails[]}} | null)=>{
    const updatedBatchData = batchDetails?.[loadoutItem.batchCode]?.[loadoutItem.batchId];
    const updatedQty = {caseQty:0,pieceQty:0,otherQty:0,reasonDetails:updatedBatchData}
    if(!updatedBatchData || !updatedBatchData.length){
        return updatedQty;
    }
    updatedBatchData.forEach(batch=>{
        updatedQty.caseQty+=batch.caseQty;
        updatedQty.pieceQty+=batch.pieceQty;
        updatedQty.otherQty+=batch.otherQty;
    })
    return updatedQty;
}

export const ristrictForwardNavigation=(navigate:NavigateFunction)=>{
    window.history.pushState(null, document.title, window.location.origin + "/dashboard/revert");
    navigate(-1);
}

export const markShortageQtyForLoadouts=async(loadoutList: LoadoutDetailsType[])=>{
    const reqBody = {
        features:loadoutList.map((loadout)=>{
            return {
                loadNumber: loadout.loadNumber,
                shortageUpdated: true,
                caseShortage:loadout.caseShortage,
                pieceShortage:loadout.pieceShortage,
                otherShortage:loadout.otherShortage
            };
        })
    }
    await ChannelkartNetworkPut(`${EnvironmentRegistry.getloadoutBaseUrl()}/v1/loadout/batch/shortage`,reqBody);
}

export const validateInvoiceDetails = async(invoice:DeliveryInvoiceDetailType, returnDetails:DeliveryInvoiceSkuDetails[])=>{
    const userDetails = JSON.parse(localStorage.authContext).user;
    const reqBody = {
        "invoiceNumber":invoice.invoiceNumber,
        "loginId":invoice.loginId,
        "source":invoice.source,
        "outletCode":invoice.outletCode,
        "type":"VisitOrder",
        "salesDetails":returnDetails.map(returnDetailsObject=>({
            "supplier":userDetails.loginId,
            "skuCode":returnDetailsObject.skuCode,
            "batchCode":returnDetailsObject.batchCode,
            "batchId":returnDetailsObject.batchId,
            "caseQuantity":returnDetailsObject.soldCaseQty,
            "pieceQuantity":returnDetailsObject.soldPieceQty,
            "otherUnitQuantity":returnDetailsObject.soldOtherQty,
            "extendedAttributes":returnDetailsObject.extendedAttributes
        })).filter(returnDetailsObject=>returnDetailsObject.caseQuantity+returnDetailsObject.otherUnitQuantity+returnDetailsObject.pieceQuantity),
        "extendedAttributes":invoice.extendedAttributes
    };
    const response:any = await ChannelkartNetworkPost("/v3/sales/validate?transformerOut=SalesValidateTransformer",reqBody);
    return response.data.feature[0];
}

export const setLoadoutDetailsStatusInDb = async (loadoutNumber: string, data: any) => {

    await insertDataIntoLocalDB("loadout_details_status", [{ data, loadoutNumber }])
  }

export const updateLoadoutDetailsStatusInDb = async (loadoutNumber: string, invoiceNumber: string, status: string) => {
    // clearLoadoutDetailsStatusInDb([loadoutNumber]);
    const currentData = (await getDataFromLocalDb("loadout_details_status", { loadoutNumber })).data;
    const newData = {
        ...(currentData.length ? currentData[0].data : {}),
        [invoiceNumber]: status
    };
    await insertDataIntoLocalDB("loadout_details_status", [{ data: newData, loadoutNumber }]);
  }
  
  export const getLoadoutDetailsStatusFromDb = async (loadoutNumber: string) => {
    const currentData = (await getDataFromLocalDb("loadout_details_status", { loadoutNumber })).data;
    return currentData;
  }
  
  export const clearLoadoutDetailsStatusInDb = async (loadoutNumber: string) => {
    const model = await getModel("loadout_details_status");
    const data = await model.find({ loadoutNumber});
    await model.deleteAll(data.data);
    // const currentData = (await getDataFromLocalDb("loadout_details_status", { loadoutNumber: { in: loadoutNumbers } })).data;
    // const newData = currentData.filter((item: any) => !loadoutNumbers.includes(item.loadoutNumber));
    
  }